import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer, LabelList, Cell,
} from 'recharts';
import PulseLogoBanner from '../assets/PULSE Logo Banner.png';

// ═══════════════════════════════════════════════════════════════════════════
// DISPLAY v4 — the building runs all day; there is NO shift that "completes".
// The board is always live: ROLLING departures (each platform shows its next
// scheduled truck; a missed truck shows red for 60 minutes, then its open
// work carries over to the next one), KPI tiles are business-day-to-date
// ("Today", 3:30 AM ET), pace is the rolling last 12 hours.
// Fixed 1920×1080 stage scaled to its container (explicit px letterboxing).
// Geist, tabular numerals, one accent, status-only saturated color, 18px
// text floor. Cards stay SELF-CONTAINED (payload as props; rotation-ready).
// Polls every 30 s; NEVER blanks (stale dot amber >2 min, red >5 min).
// All times ET. Counts only — no names anywhere.
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
const ET = 'America/New_York';

const STAGE_W = 1920, STAGE_H = 1080;

const P = {
  bg: '#F7F8FA', card: '#FFFFFF', border: '#EAECF0', grid: '#F2F4F7',
  text: '#101828', body: '#344054', sub: '#667085', faint: '#98A2B3',
  accent: '#3E63DD', track: '#EEF1F6', mute: '#E4E7EC',
  green: '#027A48', greenBg: '#ECFDF3', greenBar: '#12B76A',
  amber: '#B54708', amberBg: '#FFFAEB', amberBar: '#F79009',
  red: '#B42318', redBg: '#FEF3F2', redBar: '#F04438',
};
const FONT = "'Geist', 'Inter', -apple-system, 'Segoe UI', Roboto, sans-serif";
const SHADOW = '0 1px 2px rgba(16,24,40,0.05)';
const NUMS = { fontVariantNumeric: 'tabular-nums' };
const BIG = { ...NUMS, fontWeight: 600, letterSpacing: '-0.02em' };

const timeET = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' }) : '—');
const clockET = (d) => d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', second: '2-digit' });
const dateNowET = (d) => d.toLocaleDateString('en-US', { timeZone: ET, weekday: 'short', month: 'short', day: 'numeric' });
const pctOf = (d, t) => (t > 0 ? Math.round((100 * d) / t) : 100);
// glanceable rounding: 24475 → "24.5K"; below 1000 stays exact
const fmtK = (n) => {
  const v = n ?? 0;
  return v >= 1000 ? `${(Math.round(v / 100) / 10).toFixed(1)}K` : String(v);
};

const OPEN_STATUSES = ['on_track', 'at_risk', 'late', 'missed', 'no_pickers', 'no_standard', 'unscheduled', 'waiting'];

const STATUS = {
  on_track: { label: 'On track', fg: P.green, bg: P.greenBg },
  at_risk: { label: 'Tight', fg: P.amber, bg: P.amberBg },
  late: { label: "Won't make it", fg: P.red, bg: P.redBg },
  missed: { label: 'Missed', fg: P.red, bg: P.redBg },
  no_pickers: { label: 'No pickers', fg: P.amber, bg: P.amberBg },
  no_standard: { label: 'No standard', fg: P.sub, bg: P.grid },
  waiting: { label: 'Waiting', fg: P.sub, bg: P.grid },
  no_truck_today: { label: 'No truck in next 24h', fg: P.sub, bg: P.grid },
  unscheduled: { label: 'Unscheduled', fg: P.sub, bg: P.grid },
  picked: { label: 'Picked', fg: P.green, bg: P.greenBg },
};

const Pill = ({ status, text, size = 18 }) => {
  const s = STATUS[status] || STATUS.unscheduled;
  return (
    <span style={{ background: s.bg, color: s.fg, fontSize: size, fontWeight: 600, padding: size >= 24 ? '6px 18px' : '4px 12px', borderRadius: 999, whiteSpace: 'nowrap', ...NUMS }}>
      {text || s.label}
    </span>
  );
};

const Card = ({ children, style }) => (
  <div style={{ background: P.card, border: `1px solid ${P.border}`, borderRadius: 12, boxShadow: SHADOW, padding: 28, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden', ...style }}>
    {children}
  </div>
);

const CardTitle = ({ children, sub }) => (
  <div style={{ marginBottom: 6 }}>
    <div style={{ fontSize: 24, fontWeight: 600, color: P.text, lineHeight: 1.2 }}>{children}</div>
    {sub && <div style={{ fontSize: 20, color: P.sub, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── KPI tiles (business day to date — "Today") ──────────────────────────────
export const KpiTiles = ({ data }) => {
  const b = data?.building, perf = data?.performance;
  // "Should be here by now" tick: if today's work were spread evenly from
  // the business-day start (3:30 AM ET) to the LAST upcoming departure,
  //   expected_pct = clamp((now − bdStart) / (lastDeparture − bdStart), 0..1) × 100
  // (server-side). Fill amber >10 points behind the tick, red >25.
  const expected = data?.expected_pct ?? null;
  const paceColor = (pct) => {
    if (expected == null || pct == null) return P.accent;
    const gap = expected - pct;
    return gap > 25 ? P.redBar : gap > 10 ? P.amberBar : P.accent;
  };
  const avg30 = perf?.avg_30d_pct;
  const stdPct = perf?.pct_to_standard;
  const stdBar = stdPct == null ? P.mute : stdPct >= 100 ? P.greenBar : (avg30 != null && stdPct < avg30) ? P.amberBar : P.accent;
  const tiles = [
    { label: 'Units', done: b?.units.done ?? 0, total: b?.units.total ?? 0, tick: true },
    { label: 'Lines', done: b?.lines.done ?? 0, total: b?.lines.total ?? 0, tick: true },
    { label: 'OBDs', done: b?.obds.done ?? 0, total: b?.obds.total ?? 0, tick: true },
    {
      label: '% to standard', pct: stdPct, bar: stdBar, barPct: stdPct ?? 0,
      context: perf ? `30-day avg ${avg30 ?? '—'}% · LPH ${perf.lph_actual ?? '—'} vs ${perf.lph_at_standard ?? '—'}` : '',
    },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, height: '100%' }}>
      {tiles.map((t) => {
        const pct = t.pct !== undefined ? t.pct : pctOf(t.done, t.total);
        const barPct = t.pct !== undefined ? t.barPct : pctOf(t.done, t.total);
        const fill = t.tick ? paceColor(pct) : t.bar;
        return (
          <Card key={t.label} style={{ padding: '12px 24px 14px', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 18, fontWeight: 500, color: P.sub, lineHeight: 1.2 }}>{t.label}</div>
            <div style={{ fontSize: 64, lineHeight: 1, color: P.text, ...BIG }}>
              {pct != null ? `${pct}%` : '—'}
            </div>
            <div style={{ fontSize: 18, color: P.sub, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...NUMS }}>
              {t.pct !== undefined
                ? t.context
                : `${fmtK(t.done)} of ${fmtK(t.total)} · Today${t.tick && expected != null ? ` · expected ${expected}%` : ''}`}
            </div>
            <div style={{ position: 'relative', height: 6, background: P.track, borderRadius: 3, marginTop: 6, marginBottom: 2 }}>
              <div style={{ height: '100%', width: `${Math.min(100, barPct)}%`, background: fill, borderRadius: 3 }} />
              {t.tick && expected != null && (
                <div style={{ position: 'absolute', top: -3, bottom: -3, left: `${Math.min(100, expected)}%`, width: 2, background: P.text, borderRadius: 1 }} />
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

// ── Departures (hero, rolling) ───────────────────────────────────────────────
export const DepartureCard = ({ data, style }) => {
  const deps = data?.byDeparture || [];
  const rows_ = deps.filter((d) => OPEN_STATUSES.includes(d.status));
  const noTruck = deps.filter((d) => d.status === 'no_truck_today');
  // the End-of-shift checkpoint always renders, as the LAST row
  const endRow = rows_.find((d) => d.is_shift_end) || null;
  const truckRows = rows_.filter((d) => !d.is_shift_end);
  const shown = endRow ? [...truckRows.slice(0, 3), endRow] : truckRows.slice(0, 4);
  const more = truckRows.length - (endRow ? 3 : 4);
  const footerBits = [];
  const recent = (data?.recently_departed || []).slice(0, 3);
  if (recent.length) footerBits.push(`Recently departed: ${recent.map((r) => `${timeET(r.departed_at)} ${r.platform} ${r.pct}%`).join(' · ')}`);
  if (noTruck.length) footerBits.push(`No truck in next 24h: ${noTruck.flatMap((d) => d.platforms).join(' · ')}`);
  if (more > 0) footerBits.push(`+${more} more`);
  // (end checkpoint never counts toward the +N more)
  return (
    <Card style={{ ...style, padding: '22px 28px' }}>
      <CardTitle>Departures</CardTitle>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {shown.map((d, i) => {
          const p = pctOf(d.units.done, d.units.total);
          const linesLeft = (d.lines?.total ?? 0) - (d.lines?.done ?? 0);
          const missed = d.status === 'missed';
          const needText = d.pickers_needed != null
            ? (d.past_due ? `needs ${d.pickers_needed} to clear in 1 hr` : `needs ${d.pickers_needed} pickers`)
            : null;
          const carried = d.carried_over_obds > 0 ? `incl. ${d.carried_over_obds} carried over` : null;
          const isEnd = !!d.is_shift_end;
          return (
            <div key={i} style={{ flex: 1, display: 'grid', gridTemplateColumns: '270px 1fr 250px', gap: 24, alignItems: 'center', borderBottom: i < shown.length - 1 ? `1px solid ${P.border}` : 'none', minHeight: 0 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 26, fontWeight: 600, color: P.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...NUMS }}>
                  {d.departure_at ? timeET(d.departure_at) : '—'}
                </div>
                <div style={{ fontSize: 18, color: P.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {isEnd ? 'End of shift · all remaining work' : d.platforms.join(' · ')}
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1, height: 12, background: P.track, borderRadius: 6 }}>
                    <div style={{ height: '100%', width: `${Math.min(100, p)}%`, background: (missed || d.status === 'late') ? P.redBar : P.accent, borderRadius: 6 }} />
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: P.body, width: 56, textAlign: 'right', ...NUMS }}>{p}%</div>
                </div>
                <div style={{ fontSize: 18, color: P.sub, marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...NUMS }}>
                  {linesLeft.toLocaleString()} lines left{carried ? ` · ${carried}` : ''}{needText ? ` · ${needText}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, color: P.sub, lineHeight: 1.2, marginBottom: 3, ...NUMS }}>
                  {missed ? `Open: ${p}% picked` : d.projected_finish ? `Done ~${timeET(d.projected_finish)}` : '—'}
                </div>
                <Pill status={d.status} />
              </div>
            </div>
          );
        })}
        {shown.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 600, color: P.green }}>
            Nothing open for the next trucks
          </div>
        )}
      </div>
      {footerBits.length > 0 && (
        <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 9, marginTop: 9, fontSize: 18, color: P.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...NUMS }}>
          {footerBits.join('   ·   ')}
        </div>
      )}
    </Card>
  );
};

// ── Pace: rolling last 12 hours ──────────────────────────────────────────────
const PACE_MIN_LINES = 100; // hours with fewer scored lines render muted, unlabeled
export const PaceCard = ({ data, style }) => {
  const avg = data?.pace?.avg_30d_pct;
  const hourly = data?.pace?.hourly || []; // server: 12 slots, empty hours null
  const rows = hourly.map((h) => ({ ...h, thin: (h.lines || 0) < PACE_MIN_LINES }));
  const hasBars = rows.some((r) => r.pct != null);
  const maxVal = Math.max(avg || 0, ...rows.map((r) => r.pct || 0));
  const yMax = Math.max(150, Math.ceil(maxVal / 50) * 50);
  const yTicks = []; for (let t = 0; t <= yMax; t += 50) yTicks.push(t);
  return (
    <Card style={style}>
      <CardTitle sub="Hourly % to standard · last 12 hours">Pace vs standard</CardTitle>
      {!hasBars ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.faint, fontSize: 20 }}>No scored work in the last 12 hours</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 24, right: 96, bottom: 0, left: -14 }} barCategoryGap="30%">
              <CartesianGrid vertical={false} stroke={P.grid} />
              {/* 12 slots: label every other hour so 20px ticks never collide */}
              <XAxis dataKey="hour" interval={1} tick={{ fontSize: 20, fill: P.sub, fontFamily: FONT }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, yMax]} ticks={yTicks} tick={{ fontSize: 20, fill: P.sub, fontFamily: FONT }} axisLine={false} tickLine={false} />
              <ReferenceLine y={100} stroke={P.faint} strokeDasharray="6 4" label={{ value: '100%', position: 'right', fill: P.sub, fontSize: 18, fontFamily: FONT }} />
              {avg != null && avg !== 100 && (
                <ReferenceLine y={avg} stroke={P.faint} strokeDasharray="3 4" label={{ value: `30d ${avg}%`, position: 'right', fill: P.sub, fontSize: 18, fontFamily: FONT }} />
              )}
              <Bar dataKey="pct" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                {rows.map((r, i) => <Cell key={i} fill={r.thin ? P.mute : P.accent} />)}
                <LabelList dataKey="pct" content={({ x, y, width, value, index }) => (value == null || rows[index]?.thin ? null : (
                  <text x={x + width / 2} y={y - 7} textAnchor="middle" fontSize={18} fill={P.sub} fontFamily={FONT}>{value}</text>
                ))} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

// ── Work left ────────────────────────────────────────────────────────────────
export const WorkLeftCard = ({ data, style }) => {
  const all = (data?.remainingByCategory || []).filter((c) => c.std_hours > 0.05);
  // top 6 + Other so 18px bars keep air in the bottom row
  let shownCount = Math.min(6, all.length);
  const otherOf = (n) => all.slice(n).reduce((a, c) => a + c.std_hours, 0);
  while (shownCount < all.length && shownCount < 6
    && otherOf(shownCount) > all[shownCount - 1].std_hours) shownCount++;
  const top = all.slice(0, shownCount);
  const other = otherOf(shownCount);
  const rows = [
    ...top.map((c) => ({ name: c.category, h: Math.round(c.std_hours * 10) / 10 })),
    ...(other > 0.05 ? [{ name: 'Other', h: Math.round(other * 10) / 10 }] : []),
  ];
  const totalH = Math.round(all.reduce((a, c) => a + c.std_hours, 0) * 10) / 10;
  return (
    <Card style={{ ...style, padding: '16px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 4 }}>
        <span style={{ fontSize: 24, fontWeight: 600, color: P.text }}>Work left</span>
        <span style={{ fontSize: 20, color: P.sub, ...NUMS }}>{totalH} std hours left</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.green, fontSize: 20, fontWeight: 600 }}>Nothing left</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 62, bottom: 0, left: 0 }} barCategoryGap="12%">
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={238} interval={0} tick={{ fontSize: 18, fill: P.body, fontFamily: FONT }} axisLine={false} tickLine={false} />
              <Bar dataKey="h" fill={P.accent} barSize={18} radius={[0, 6, 6, 0]} isAnimationActive={false}>
                <LabelList dataKey="h" position="right" style={{ fontSize: 18, fill: P.sub, fontFamily: FONT, ...NUMS }} formatter={(v) => `${v}h`} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

// ── People ───────────────────────────────────────────────────────────────────
export const PeopleCard = ({ data, style }) => {
  const p = data?.people;
  const pickers = p?.pickers ?? 0;
  const open = (data?.byDeparture || []).filter((d) => OPEN_STATUSES.includes(d.status));
  const need = open.find((d) => d.pickers_needed != null);
  const needColor = !need ? P.sub : need.pickers_needed > pickers ? P.red : P.green;
  const needLabel = !open.length
    ? 'no open departures'
    : need
      ? (need.past_due
        ? `needs ${need.pickers_needed} to clear ${timeET(need.departure_at)} in 1 hr`
        : `needs ${need.pickers_needed} for ${timeET(need.departure_at)}`)
      : 'open work has no scheduled truck';
  const prePick = (p?.byCategory || []).find((c) => /pre-?pick/i.test(c.label || ''))?.n ?? 0;
  const cells = [['Pre-pick', prePick], ['Lunch', p?.on_lunch ?? 0], ['Break', p?.on_break ?? 0]];
  return (
    <Card style={{ ...style, padding: '16px 28px' }}>
      <CardTitle>People</CardTitle>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 0 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontSize: 44, lineHeight: 1.05, color: P.text, ...BIG }}>{pickers}</span>
            <span style={{ fontSize: 20, color: P.sub }}>picking</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 500, color: needColor, marginTop: 2, ...NUMS }}>
            {needLabel}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, borderTop: `1px solid ${P.border}`, paddingTop: 14 }}>
          {cells.map(([l, v]) => (
            <div key={l}>
              <div style={{ fontSize: 28, lineHeight: 1.1, color: P.text, ...BIG }}>{v}</div>
              <div style={{ fontSize: 18, lineHeight: 1.2, color: P.sub }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

// ── Queue ────────────────────────────────────────────────────────────────────
export const QueueCard = ({ data, style }) => {
  const b = data?.building;
  const cells = [
    ['Available', b?.status.available], ['In progress', b?.status.in_progress],
    ['Paused', b?.status.paused], ['Added today', b?.added_today],
  ];
  return (
    <Card style={{ ...style, padding: '16px 28px' }}>
      <CardTitle>Queue</CardTitle>
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 8, alignItems: 'center' }}>
        {cells.map(([l, v]) => (
          <div key={l}>
            <div style={{ fontSize: 34, lineHeight: 1.1, color: P.text, ...BIG }}>{v ?? '—'}</div>
            <div style={{ fontSize: 18, lineHeight: 1.2, color: P.sub, whiteSpace: 'nowrap' }}>{l}</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ── outlook grade (always-live headline) ─────────────────────────────────────
// No calculations here: status, sentence pieces, basis and warnings all come
// from the engine's outlook object embedded in /api/display/summary. The
// grade evaluates the UPCOMING departures (carry-over included); with no
// open work at all it goes neutral gray.
const GRADE = {
  between_shifts: { label: 'Between shifts', c: '#667085' },
  on_track: { label: 'On track', c: '#22A06B' },
  tight: { label: 'Tight', c: '#F5A524' },
  wont_make_it: { label: "Won't make it", c: '#E5484D' },
  no_open: { label: 'No open work', c: '#667085' },
};
const GradeBlock = ({ data }) => {
  const o = data?.outlook;
  const g = GRADE[o?.overall || 'no_open'] || GRADE.no_open;
  // "short X labor hours (about N people)" — straight from the engine
  const shortBit = (d) => d && d.short_labor_hours > 0
    ? `short ${d.short_labor_hours} labor hours${d.short_people != null ? ` (about ${d.short_people} ${d.short_people === 1 ? 'person' : 'people'})` : ''}`
    : null;
  // BETWEEN SHIFTS: neutral gray, open work waiting, nothing graded
  if (o && o.state === 'between_shifts') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={{ background: '#6670851A', color: '#667085', fontSize: 40, fontWeight: 700, lineHeight: 1, padding: '6px 20px', borderRadius: 12, whiteSpace: 'nowrap' }}>
            Between shifts
          </span>
          <span style={{ fontSize: 28, fontWeight: 500, color: P.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...NUMS }}>
            {o.next_shift?.name} starts {timeET(o.next_shift?.start_at)}
          </span>
        </div>
        <div style={{ fontSize: 18, color: P.sub, lineHeight: 1.25, marginTop: 3, ...NUMS }}>
          Waiting: {o.waiting?.obds ?? 0} OBDs · {(o.waiting?.lines ?? 0).toLocaleString()} lines · {o.waiting?.std_hours ?? 0} std hours
        </div>
      </div>
    );
  }
  let sentence = '';
  if (o) {
    const worst = o.departures.find(d => d.status === o.overall) || o.departures[0];
    const short = shortBit(worst);
    // checkpoint naming: "End of shift (2:30 AM)" vs "3:30 AM truck"
    const cpName = worst
      ? (worst.is_shift_end ? `End of shift (${timeET(worst.departure_at)})` : `${timeET(worst.departure_at)} truck`)
      : '';
    // priority-first covering: the engine names which categories won't make
    // the checkpoint (secondary ones first — priority covered in rank order)
    const missBit = worst && (worst.missed_groups || []).length
      ? ` · will miss: ${worst.missed_groups.join(', ')}` : '';
    if (o.overall === 'wont_make_it' && worst) {
      sentence = worst.past_due
        ? `${cpName}: ${worst.cum_std_hours} labor hours of work still open${missBit}`
        : `${cpName}: ${short || 'behind'}${missBit}`;
    } else if (o.overall === 'tight' && worst) {
      sentence = `${cpName}: tight${short ? ` — ${short}` : ''}${missBit}`;
    } else if (o.overall === 'on_track') {
      sentence = 'All trucks on track';
    } else {
      sentence = 'No departures with open work';
    }
  }
  // "X of Y hrs estimated without a standard" appears only when some open
  // work had no labor standard and was estimated via the 30d min/line fallback
  const fb = o?.std_hours;
  const fbNote = fb && fb.fallback > 0 ? ` · ${fb.fallback} of ${fb.total} hrs estimated without a standard` : '';
  const basis = o
    ? `${o.inputs.labor_source === 'roster'
        ? `Based on ${o.inputs.labor_hours} labor hours`
        : `No shift hours set — using ${o.inputs.headcount} clocked in`} · assumes ${o.inputs.pace_pct}% pace${o.inputs.pace_source === 'shift' ? " (tonight's pace)" : o.inputs.pace_source === 'avg_30d' ? ' (30-day avg)' : ''}${fbNote}`
    : '';
  const warns = o?.warnings || [];
  // the grade row is a fixed 152px: with the idle line present there is
  // room for ONE warning line (the +N more counter keeps the rest visible)
  const maxWarns = (o?.inputs?.idle_pickers > 0) ? 1 : 2;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <span style={{ background: `${g.c}1A`, color: g.c, fontSize: 40, fontWeight: 700, lineHeight: 1, padding: '6px 20px', borderRadius: 12, whiteSpace: 'nowrap' }}>
          {g.label}
        </span>
        <span style={{ fontSize: 28, fontWeight: 500, color: P.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...NUMS }}>
          {sentence}
        </span>
      </div>
      <div style={{ fontSize: 18, color: P.sub, lineHeight: 1.25, marginTop: 3, ...NUMS }}>{basis}</div>
      {o?.inputs?.idle_pickers > 0 && (
        <div style={{ fontSize: 18, fontWeight: 600, color: '#B54708', lineHeight: 1.25, marginTop: 2, ...NUMS }}>
          {o.inputs.idle_pickers} of {o.inputs.headcount} pickers idle {o.inputs.idle_warning_minutes}+ min
        </div>
      )}
      {warns.slice(0, maxWarns).map((w, i) => (
        <div key={w.group_id ?? w.type} style={{ fontSize: 18, fontWeight: 600, color: w.type === 'config' ? '#B54708' : '#E5484D', lineHeight: 1.25, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {w.message}{w.type === 'config' ? '' : ' ET'}{i === maxWarns - 1 && warns.length > maxWarns ? `   ·   +${warns.length - maxWarns} more` : ''}
        </div>
      ))}
    </div>
  );
};

// ── page ─────────────────────────────────────────────────────────────────────
function Display() {
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [now, setNow] = useState(new Date());
  // explicit px letterboxing: scale + offsets computed from the container so
  // no browser ambiguity about percentage translates vs transform order
  const [fit, setFit] = useState({ s: 1, x: 0, y: 0 });
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById('pulse-geist-font')) {
      const l = document.createElement('link');
      l.id = 'pulse-geist-font';
      l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap';
      document.head.appendChild(l);
    }
    const el = wrapRef.current;
    if (!el) return;
    const doFit = () => {
      const w = el.clientWidth, h = el.clientHeight;
      const s = Math.min(w / STAGE_W, h / STAGE_H);
      setFit({ s, x: (w - STAGE_W * s) / 2, y: (h - STAGE_H * s) / 2 });
    };
    doFit();
    const ro = new ResizeObserver(doFit);
    ro.observe(el);
    window.addEventListener('resize', doFit);
    return () => { ro.disconnect(); window.removeEventListener('resize', doFit); };
  }, []);

  // ── silent token renewal ──────────────────────────────────────────────────
  // Sessions are FIXED at login (720h for the display role), so a TV left on
  // would eventually expire and strand the board. Renew on mount and then
  // every 12h; a failed attempt retries every 10 min until one succeeds.
  // The board NEVER navigates to login and the summary poll uses plain fetch
  // (not pulseRequest, which clears the stored token on any 401 — exactly the
  // behavior that would make an expiry permanent here). If renewal ultimately
  // can't recover (e.g. the session was revoked), the stale dot is the only
  // degradation and a human re-login fixes it.
  const lastRenew = useRef(0);
  const renewToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const res = await fetch(`${API_BASE}/api/auth/renew`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json().catch(() => null);
        if (d && d.token) {
          localStorage.setItem('authToken', d.token);
          lastRenew.current = Date.now();
        }
      }
      // non-ok: leave the current token alone; the retry cadence below and
      // renew-on-401 keep trying
    } catch { /* offline — retried on the 10-min cadence */ }
  }, []);

  useEffect(() => {
    renewToken();
    const t = setInterval(() => {
      if (Date.now() - lastRenew.current > 12 * 60 * 60 * 1000) renewToken();
    }, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [renewToken]);

  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE}/api/display/summary`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 401) { renewToken(); return; } // keep last good data; recover once renewed
      const d = await res.json().catch(() => null);
      if (res.ok && d && d.building) { setData(d); setUpdatedAt(new Date()); }
    } catch { /* keep last good data — the stale dot does the talking */ }
  }, [renewToken]);

  useEffect(() => {
    fetchSummary();
    timer.current = setInterval(fetchSummary, 30000);
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(timer.current); clearInterval(tick); };
  }, [fetchSummary]);

  const staleSec = updatedAt ? (now - updatedAt) / 1000 : Infinity;
  const dotColor = staleSec > 300 ? P.redBar : staleSec > 120 ? P.amberBar : P.greenBar;

  return (
    <div ref={wrapRef} style={{ position: 'fixed', inset: 0, background: P.bg, overflow: 'hidden', zIndex: 1200 }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, width: STAGE_W, height: STAGE_H,
        transform: `translate(${fit.x}px, ${fit.y}px) scale(${fit.s})`, transformOrigin: 'top left',
        fontFamily: FONT, color: P.text, background: P.bg,
        display: 'grid', gridTemplateRows: '112px 124px 152px 336px 244px', rowGap: 20, padding: '0 32px 32px', boxSizing: 'border-box',
      }}>
        {/* 1 · header — white band (the PULSE logo asset has a baked-in white
            background; no transparent version exists in the repo) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, margin: '0 -32px', padding: '0 32px', background: '#FFFFFF', borderBottom: `1px solid ${P.border}` }}>
          <img src={PulseLogoBanner} alt="PULSE" style={{ height: 108, width: 'auto', display: 'block' }} />
          <span style={{ fontSize: 19, fontWeight: 500, color: P.body, borderLeft: `1px solid ${P.border}`, paddingLeft: 18 }}>ATL DC</span>
          <span style={{ fontSize: 18, color: P.text }}>{dateNowET(now)}</span>
          <span style={{ marginLeft: 'auto', fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em', color: P.text, ...NUMS }}>{clockET(now)} <span style={{ fontSize: 18, color: P.sub, fontWeight: 500 }}>ET</span></span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 18, color: P.sub, ...NUMS }}>
            <span style={{ width: 9, height: 9, borderRadius: 5, background: dotColor, display: 'inline-block' }} />
            {updatedAt ? `Updated ${timeET(updatedAt.toISOString())}` : 'Waiting for data…'}
          </span>
        </div>
        {/* 2 · outlook grade — always live; there is no shift-complete mode */}
        <GradeBlock data={data} />
        {/* 3 · KPI row (business day to date) */}
        <KpiTiles data={data} />
        {/* 4 · main row */}
        <div style={{ display: 'grid', gridTemplateColumns: '8fr 4fr', gap: 24, minHeight: 0 }}>
          <DepartureCard data={data} />
          <PaceCard data={data} />
        </div>
        {/* 5 · bottom row */}
        <div style={{ display: 'grid', gridTemplateColumns: '6fr 3fr 3fr', gap: 24, minHeight: 0 }}>
          <WorkLeftCard data={data} />
          <PeopleCard data={data} />
          <QueueCard data={data} />
        </div>
      </div>
    </div>
  );
}

export default Display;
